// =============================================================
//  NTOA SITE CONTENT  —  EDIT EVERYTHING HERE
// -------------------------------------------------------------
//  • Change any text or PRICE below and the site updates live.
//  • comingSoon: true  ->  shows a small "Coming Soon" badge
//  • hidden: true       ->  removes the item completely (no badge)
//
//  PRICING: edit membership.tiers[].price  (e.g. "Free", "$99/yr",
//           "$29/mo", "via Zion"). Add/remove feature lines in
//           membership.tiers[].features.
//
//  HIDE THE FUEL DISCOUNT: set hidden: true on the "Fuel Savings"
//  item in benefits.items AND on the "Fuel discount network access"
//  line in membership.tiers. (Both are pre-set below — just flip
//  hidden from false to true.)
// =============================================================

export const CONTENT = {
  brand: {
    name: "NTOA",
    full: "National Transportation Operators Association",
    domain: "ntoassoc.com",
  },

  hero: {
    overline: "National Transportation Operators Association",
    titleLine1: "THE BACKBONE OF",
    titleLine2: "AMERICA'S ROADS.",
    subtitle:
      "Helping truck drivers save money, protect their rigs, and connect with industry-leading partners. Membership through Zion or join us direct — every driver is welcome.",
    primaryCta: "Join NTOA Today",
    secondaryCta: "Member Login",
  },

  // Small stat row in the About section
  stats: [
    { value: "2026", label: "Newly Launched" },
    { value: "48", label: "States Welcome" },
    { value: "24/7", label: "Support Access" },
  ],

  // Scrolling ribbon items
  marquee: [
    "Built for Owner-Operators",
    "24/7 Roadside Support",
    "Trusted Industry Partners",
    "Real Truckers, Real Community",
    "Insurance Through Zion",
    "New Savings Programs Coming Soon",
  ],

  about: {
    chapter: "Chapter 01 — Built for the Road",
    heading: "We work as hard as the drivers we stand behind.",
    paragraphs: [
      "The National Transportation Operators Association exists for one reason: to give truck drivers and owner-operators the leverage, savings, and support that big fleets take for granted. Whether you found us through Zion Underwriting or joined on your own, you're part of a community built on respect for the miles you put in.",
      "No corporate runaround. Just honest benefits, trusted partners, and people who understand life on the highway.",
    ],
    badge: "Owner-Operator · 22 yrs on the road",
  },

  benefits: {
    chapter: "Chapter 02 — Member Benefits",
    heading: "Everything you need to run leaner.",
    // icon options: fuel | insurance | roadside | community | health | partners
    // size options: "large" (big feature card) | "tall" | "small"
    items: [
      {
        icon: "fuel",
        title: "Fuel Savings Network",
        desc: "Discounted diesel at thousands of stops nationwide — negotiated rates that add up fast, mile after mile.",
        stat: "Up to 65¢",
        statUnit: "/gal",
        size: "large",
        comingSoon: true,
        hidden: false, // set true to remove the Fuel Savings card entirely
      },
      {
        icon: "insurance",
        title: "Insurance & Coverage",
        desc: "Competitive rig, cargo, and liability coverage built for operators.",
        size: "small",
        comingSoon: false,
      },
      {
        icon: "roadside",
        title: "24/7 Roadside",
        desc: "Breakdowns don't wait. Neither do we — support any hour, any state.",
        size: "small",
        comingSoon: false,
      },
      {
        icon: "community",
        title: "Driver Community",
        desc: "Connect with operators who've been where you are — advice, loads, and real camaraderie.",
        size: "tall",
        comingSoon: false,
      },
      {
        icon: "health",
        title: "Medical & Dental",
        desc: "Group-rate medical, dental, and wellness plans for you and your family.",
        size: "small",
        comingSoon: true,
      },
      {
        icon: "partners",
        title: "Industry Partners",
        desc: "Exclusive deals on tires, parts, tech, and services from trusted names.",
        size: "small",
        comingSoon: false,
      },
    ],
  },

  membership: {
    chapter: "Chapter 03 — Membership",
    heading: "Simple pricing. Real savings.",
    subtitle:
      "Choose the plan that fits your miles. Every membership unlocks the NTOA network — join direct or through Zion Underwriting.",
    tiers: [
      {
        name: "Annual",
        subtitle: "Renews every year",
        listPrice: "$49.99",
        salePrice: "$29.99",
        savings: "Save $20.00",
        note: "Best value",
        highlight: true,
        hidden: false,
        cta: "Get Started",
        features: [],
      },
      {
        name: "Lifetime",
        subtitle: "One-time payment",
        listPrice: "$999.99",
        salePrice: "$699.99",
        savings: "Save $300.00",
        note: "One-time payment. Lifetime perks.",
        highlight: false,
        hidden: false,
        cta: "Get Started",
        features: [],
      },
      {
        name: "Monthly",
        subtitle: "Renews every month",
        listPrice: "$12.99",
        salePrice: "$7.99",
        savings: "Save $5.00",
        note: "Renews each month. Cancel anytime.",
        highlight: false,
        hidden: false,
        cta: "Get Started",
        features: [],
      },
    ],
  },

  contact: {
    overline: "Get in touch",
    headingLine1: "Keep America",
    headingLine2: "Moving.",
    joinCta: "Become a Member",
    loginCta: "Member Login",
    email: "info@ntoassoc.com",
    phone: "1-800-555-0199",
    location: "United States · Nationwide",
  },

  promo: {
    enabled: false,
    bannerText: "🎉 Join this month and be entered to win a $500 fuel card!",
    bannerCtaLabel: "Enter Now",
    bannerCtaUrl: "https://zionuw.com/ntoa",
    flyerUrl: "",
    flyerAlt: "NTOA promotion",
    showPopup: true,
    startDate: "",
    endDate: "",
  },

  legal: {
    purpose:
      "The National Transportation Operators Association exists to give truck drivers and owner-operators the collective strength, savings, and support that large fleets take for granted — helping independent drivers save money, protect their livelihood, and connect with trusted industry partners.",
    bylaws:
      "ARTICLE I — NAME\nThe name of the organization shall be the National Transportation Operators Association (\"NTOA\").\n\nARTICLE II — PURPOSE\nNTOA is organized to advance the interests of truck drivers and owner-operators through group savings programs, member benefits, education, and connection with reputable industry partners.\n\nARTICLE III — MEMBERSHIP\nMembership is open to any driver or owner-operator who joins directly or through an approved partner such as Zion Underwriting.\n\nARTICLE IV — DUES\nMembership dues and any promotional pricing shall be set by NTOA and published on the official website.\n\nARTICLE V — GOVERNANCE\nNTOA shall be governed in accordance with policies adopted by its leadership.\n\nARTICLE VI — AMENDMENTS\nThese bylaws may be amended at any time by NTOA leadership; the current version posted on ntoassoc.com governs.",
  },
};

export const LINKS = {
  login: "https://zionuw.com/insured/login",
  signup: "https://zionuw.com/ntoa",
};
